// src/components/programs/ShowProgram/ProgramActions.jsx
import { Box, Button, Stack } from "@mui/material";
import {  Description, Edit, Settings, Print, CheckCircle, Cancel } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import PdfSectionsModal from "./PdfSectionsModal";
import PdfExportModal from "./PdfExportModal";
import toast from "react-hot-toast";
// import useConfirmProgram from "../../../hooks/programs/useConfirmProgram";
import useCloseProgram from "../../../hooks/programs/useCloseProgram";
import useConvertProgramCurrency from "../../../hooks/programs/useConvertProgramCurrency";
import useGetCurrencies from "../../../hooks/currencies/useGetCurrencies";
import { usePermissions } from "../../../auth/PermissionsContext";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";

const ProgramActions = ({ program, confirmProgramMutate, isConfirming }) => {
    const navigate = useNavigate();
    const { i18n } = useTranslation(); 
    const lang = i18n.language;
    const [openPdfModal, setOpenPdfModal] = useState(false); 
    const [openExportModal, setOpenExportModal] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState(program?.display_currency?.id || program?.currency?.id || "");
    const { has } = usePermissions();

    // const { confirmProgramMutate, isPending: isConfirming } = useConfirmProgram();
    const { closeProgramMutate, isPending: isClosing } = useCloseProgram();
    const { convertCurrencyMutate, isPending: isConverting } = useConvertProgramCurrency();
    const { currencies, isLoading: isLoadingCurrencies } = useGetCurrencies(1, 1000); // Get all currencies

    const perms = {
        confirm: has("programs_confirm"),
        close: has("programs_close"),
        edit: has("programs_edit"),
    };

    /**
     * ✅ الطباعة الاحترافية - نفس طريقة Ctrl+P
     * مع الحفاظ على جميع الـ Styles والتصاميم
     */
    const handlePrint = () => {
        try {
            // الحصول على محتوى البرنامج المراد طباعته
            const programContent = document.getElementById('program-content');
            
            if (!programContent) {
                throw new Error(
                    lang === 'ar' 
                        ? 'لم يتم العثور على محتوى البرنامج' 
                        : 'Program content not found'
                );
            }

            // إخفاء جميع العناصر غير المطلوبة
            const noprint = document.querySelectorAll('.no-print');
            const originalDisplays = [];
            
            noprint.forEach((el, index) => {
                originalDisplays[index] = el.style.display;
                el.style.display = 'none';
            });

            // طباعة بعد تحديث الـ DOM قليلاً
            setTimeout(() => {
                window.print();
                
                // إعادة إظهار العناصر المخفية بعد الطباعة
                setTimeout(() => {
                    noprint.forEach((el, index) => {
                        el.style.display = originalDisplays[index];
                    });
                }, 100);
            }, 100);
            
        } catch (error) {
            console.error('Print error:', error);
            
            // إعادة إظهار العناصر في حالة الخطأ
            const noprint = document.querySelectorAll('.no-print');
            noprint.forEach(el => el.style.display = '');
            
            toast.error(
                lang === 'ar' 
                    ? 'حدث خطأ أثناء الطباعة. يرجى المحاولة مرة أخرى.' 
                    : 'An error occurred while printing. Please try again.'
            );
        }
    };

    const handlePDF = () => {
        setOpenExportModal(true);
    };

    // const handleShare = () => {
    //     console.log("Share program");
    //     // TODO: Implement share functionality
    // };

    const handleEdit = () => {
        navigate(`/programs/${program.id}/edit`);
    };

    const handleOpenPdfModal = () => {
        setOpenPdfModal(true);
    };

    const handleClosePdfModal = () => {
        setOpenPdfModal(false);
    };

    const handleCloseExportModal = () => {
        setOpenExportModal(false);
    };

    const handleConfirm = () => {
        if (!perms.confirm) {
            toast.error(lang === 'ar' ? "ليس لديك صلاحية التأكيد" : "No confirm permission");
            return;
        }
        confirmProgramMutate(program.id);
    };

    const handleClose = () => {
        if (!perms.close) {
            toast.error(lang === 'ar' ? "ليس لديك صلاحية الإغلاق" : "No close permission");
            return;
        }
        closeProgramMutate(program.id);
    };

    const handleCurrencyChange = (event) => {
        const currencyId = event.target.value;
        setSelectedCurrency(currencyId);
        
        // Only convert if it's different from current display currency or original currency
        const currentCurrencyId = program?.display_currency?.id || program?.currency?.id;
        if (currencyId && currencyId !== currentCurrencyId) {
            convertCurrencyMutate({ id: program.id, currencyId });
        }
    };

    return (
        <>
            <Box
                className="no-print"
                sx={{
                    mb: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1
                }}
            >
                {/* Left Side - Customize Button */}
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Settings />}
                    onClick={handleOpenPdfModal}
                    color="info"
                >
                    {lang === 'ar' ? "تخصيص سياسات PDF" : "Customize PDF Sections"}
                </Button>

                {/* Right Side - Action Buttons */}
                <Stack direction="row" spacing={1} gap={2} flexWrap="wrap" alignItems="center">
                    {/* Currency Select */}
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>
                            {lang === 'ar' ? "العملة" : "Currency"}
                        </InputLabel>
                        <Select
                            value={selectedCurrency}
                            onChange={handleCurrencyChange}
                            disabled={isLoadingCurrencies || isConverting}
                            label={lang === 'ar' ? "العملة" : "Currency"}
                            sx={{
                                '& .MuiSelect-select': {
                                    fontSize: '14px',
                                    fontWeight: 500,
                                },
                            }}
                        >
                            {currencies?.items?.map((currency) => (
                                <MenuItem key={currency.id} value={currency.id} sx={{ fontSize: '14px' }}>
                                    <span style={{ fontWeight: 600, marginRight: lang === 'ar' ? 0 : '8px', marginLeft: lang === 'ar' ? '8px' : 0 }}>
                                        {currency.symbol}
                                    </span>
                                    <span>
                                        {currency.name[lang] || currency.name.en}
                                    </span>
                                    <span style={{ marginLeft: lang === 'ar' ? 0 : 'auto', marginRight: lang === 'ar' ? 'auto' : 0, fontSize: '12px', color: '#999' }}>
                                        ({currency.code})
                                    </span>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {!program.confirmed && perms.confirm && (
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<CheckCircle />}
                            onClick={handleConfirm}
                            disabled={isConfirming}
                            color="success"
                            sx={{ minWidth: 100 }}
                        >
                            {isConfirming ? (lang === 'ar' ? "جاري التأكيد..." : "Confirming...") : (lang === 'ar' ? "تأكيد" : "Confirm")}
                        </Button>
                    )}
                    {program.confirmed && !program.closed && perms.close && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Cancel />}
                            onClick={handleClose}
                            disabled={isClosing}
                            color="error"
                            sx={{ minWidth: 100 }}
                        >
                            {isClosing ? (lang === 'ar' ? "جاري الإغلاق..." : "Closing...") : (lang === 'ar' ? "إغلاق" : "Close")}
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Print />}
                        onClick={handlePrint}
                        sx={{ minWidth: 100 }}
                    >
                        {lang === 'ar' ? "طباعة" : "Print"}
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Description />}
                        onClick={handlePDF}
                        color="error"
                        disabled={!program.confirmed}
                        sx={{ minWidth: 80, gap:1, mx:0.5 }}
                    >
                        PDF
                    </Button>
                    {/* <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Share />}
                        onClick={handleShare}
                        color="info"
                        sx={{ minWidth: 90 }}
                    >
                        {lang === 'ar' ? "مشاركة" : "Share"}
                    </Button> */}
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<Edit />}
                        onClick={handleEdit}
                        sx={{ minWidth: 90, gap:1, mx:0.5 }}
                        disabled={program.closed}
                    >
                        {lang === 'ar' ? "تعديل" : "Edit"}
                    </Button>
                </Stack>
            </Box>

            {/* PDF Sections Modal */}
            <PdfSectionsModal
                open={openPdfModal}
                onClose={handleClosePdfModal}
                programId={program.id}
            />

            {/* PDF Export Modal */}
            <PdfExportModal
                open={openExportModal}
                onClose={handleCloseExportModal}
                program={program}
            />
        </>
    );
};

export default ProgramActions;
